import { VideoFormState, PromptPreset } from '../types/heygen';
import type { UpdateAvatarProjectInput, UpdateAvatarSceneInput } from '../../../types/avatar-video';
import { getPhotoId, resolutionToAspectRatio } from './avatar-lookups';
import { type Plano } from '../constants/avatar-matrix';

/**
 * Construye el body para `PUT /avatars/project/{id}` a partir del estado del form.
 * Similar a `buildPayload` pero produce el shape específico del edit endpoint —
 * escenas con `sceneId` opcional y `index` explícito.
 *
 * - Escenas del form con `sceneId` seteado → llegan al backend con el UUID,
 *   se comparan por hash contra la scene existente.
 * - Escenas sin `sceneId` (nuevas en la UI) → llegan sin id, el backend genera UUID.
 * - `dimension` NO viaja (inmutable en edit).
 */
export const buildUpdatePayload = (
  state: VideoFormState,
  presets: PromptPreset[],
  placaId: string | null,
  musicTrackId: string | null,
  selectedResolution: string,
  subtitles: boolean
): UpdateAvatarProjectInput => {
  const preset = presets.find(p => p.key === state.promptKey);
  const prompt = preset?.prompt ?? state.promptKey;
  const resolucion = resolutionToAspectRatio(selectedResolution);

  const scenes: UpdateAvatarSceneInput[] = state.scenes.map((scene, index) => {
    if (scene.onlyVoice) {
      return {
        sceneId: scene.sceneId,
        index,
        onlyVoice: true as const,
        script: scene.script
      };
    }

    const photoId = getPhotoId(scene.vestuario, scene.ambiente, resolucion, scene.plano as Plano);
    if (!photoId) {
      throw new Error(`Avatar no encontrado: ${scene.vestuario}|${scene.ambiente}|${resolucion}|${scene.plano}`);
    }

    return {
      sceneId: scene.sceneId,
      index,
      onlyVoice: false as const,
      script: scene.script,
      character: {
        type: 'talking_photo' as const,
        talking_photo_id: photoId,
        use_avatar_iv_model: true as const,
        prompt,
        talking_style: state.talkingStyle,
        matting: false
      },
      background: {
        type: 'color' as const,
        value: '#FFFFFF'
      }
    };
  });

  return {
    title: state.title,
    placaId,
    musicTrackId,
    subtitles,
    scenes
  };
};
